import React, { useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Table,
  Button,
  Card,
  Dropdown,
  Input,
  Select,
  App,
  Empty,
} from "antd";
import {
  DownOutlined,
  EllipsisOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import theme from "../../themes";
import ContentLoading, { filterControlCss } from "../../components/ContentLoading";
import {
  kpiBreakCss,
  pageShellCss,
  pageTitleCss,
  searchFieldCss,
  tableScrollCss,
  wrapRowCss,
} from "../../styles/responsive";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import CreateCampaignModal from "../../components/CreateCampaignModal";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getCampaignsThunk,
  updateCampaignThunk,
  deleteCampaignThunk,
} from "../../store/actions/campaign.action";
import { getCampaigns } from "../../services/campaign.service";
import { useNavigate } from "react-router-dom";
import { CAMPAIGN_STATUS } from "./type";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import ScheduleCampaignModal from "../../components/ScheduleCampaignModal";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { formatNumber } from "../../utils/formatDecimalNumber";
import { useChannelSettings } from "../../hooks/useChannelSettings";

const { Option } = Select;

const statusColors: Record<string, { text: string; background: string }> = {
  [CAMPAIGN_STATUS.IN_PROGRESS]: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  [CAMPAIGN_STATUS.TEST]: {
    text: theme.colors.yellow2,
    background: theme.colors.yellow3,
  },
  [CAMPAIGN_STATUS.SCHEDULE]: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  [CAMPAIGN_STATUS.COMPLETED]: {
    text: theme.colors.purple1,
    background: theme.colors.purple2,
  },
  [CAMPAIGN_STATUS.STOPPED]: {
    text: theme.colors.grey1,
    background: theme.colors.grey5,
  },
};

const normalizeChannel = (value?: string) => {
  const raw = String(value || "call").toLowerCase();
  if (raw.includes("whatsapp") || raw === "forum") return "whatsapp";
  if (raw.includes("mail") || raw.includes("email")) return "email";
  if (raw.includes("sms") || raw.includes("text")) return "sms";
  return "call";
};

const channelIcon = (key: string) => {
  if (key === "sms") return <MessageOutlined />;
  if (key === "whatsapp") return <WhatsAppOutlined />;
  if (key === "email") return <MailOutlined />;
  return <PhoneOutlined />;
};

const STATUS_FILTER_ALL = "all";

const STATUS_FILTER_VALUES = [
  STATUS_FILTER_ALL,
  CAMPAIGN_STATUS.IN_PROGRESS,
  CAMPAIGN_STATUS.TEST,
  CAMPAIGN_STATUS.SCHEDULE,
  CAMPAIGN_STATUS.COMPLETED,
  CAMPAIGN_STATUS.STOPPED,
] as const;

const dash = (value?: number | null) =>
  value === undefined || value === null ? "—" : formatNumber(value);

const Outbound: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTimeRange, setSelectedTimeRange] = useState("30d");
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_FILTER_ALL);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { loading, campaigns } = useAppSelector((state) => state.campaign);
  const { isEnabled } = useChannelSettings();
  const navigate = useNavigate();
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );
  const [rangeCampaigns, setRangeCampaigns] = useState<any[]>([]);
  const { message, modal } = App.useApp();
  const toast = ToastMessage(message);

  const getStartDate = (timeRange: string): string => {
    const now = dayjs();
    switch (timeRange) {
      case "24h":
        return now.subtract(1, "day").format("YYYY-MM-DD");
      case "7d":
        return now.subtract(7, "day").format("YYYY-MM-DD");
      case "90d":
        return now.subtract(90, "day").format("YYYY-MM-DD");
      default:
        return now.subtract(30, "day").format("YYYY-MM-DD");
    }
  };

  const getStatusLabel = (status: string) => {
    if (!status || status === STATUS_FILTER_ALL) {
      return t("outbound.statusLabels.allStatus");
    }
    const statusMap: Record<string, string> = {
      [CAMPAIGN_STATUS.ALL]: t("outbound.statusLabels.allStatus"),
      [CAMPAIGN_STATUS.SCHEDULE]: t("outbound.statusLabels.scheduled"),
      [CAMPAIGN_STATUS.IN_PROGRESS]: t("outbound.statusLabels.inProgress"),
      [CAMPAIGN_STATUS.STOPPED]: t("outbound.statusLabels.stopped"),
      [CAMPAIGN_STATUS.COMPLETED]: t("outbound.statusLabels.finished"),
      [CAMPAIGN_STATUS.TEST]: t("outbound.statusLabels.testing"),
    };
    return statusMap[status] || status;
  };

  const fetchCampaigns = async (page = currentPage) => {
    await dispatch(
      getCampaignsThunk({
        limit: pageSize,
        page,
        keyword: debouncedSearch,
        status: statusFilter === STATUS_FILTER_ALL ? "" : statusFilter,
        directionFilter: "",
        start_date: getStartDate(selectedTimeRange),
        sort: { createdAt: -1 },
      })
    ).unwrap();
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedTimeRange, statusFilter]);

  useEffect(() => {
    fetchCampaigns(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, selectedTimeRange, statusFilter]);

  useEffect(() => {
    getCampaigns({
      page: 1,
      limit: 1000,
      keyword: "",
      status: "",
      directionFilter: "",
      start_date: getStartDate(selectedTimeRange),
      sort: { createdAt: -1 },
    })
      .then((res) => setRangeCampaigns(res.data?.data?.campaigns || []))
      .catch(() => setRangeCampaigns([]));
  }, [selectedTimeRange]);

  const kpis = useMemo(() => {
    const active = rangeCampaigns.filter(
      (campaign) => campaign.status === CAMPAIGN_STATUS.IN_PROGRESS
    ).length;
    const scheduled = rangeCampaigns.filter(
      (campaign) => campaign.status === CAMPAIGN_STATUS.SCHEDULE
    ).length;
    const finished = rangeCampaigns.filter(
      (campaign) => campaign.status === CAMPAIGN_STATUS.COMPLETED
    ).length;

    const sends = { call: 0, sms: 0, whatsapp: 0, email: 0 };
    let answered = 0;
    let replyDials = 0;
    rangeCampaigns.forEach((campaign) => {
      const key = normalizeChannel(campaign.channel) as keyof typeof sends;
      const delivered =
        Number(campaign.stats?.number_of_sent) ||
        Number(campaign.stats?.number_of_calls) ||
        0;
      sends[key] += delivered;
      if (key !== "call") {
        answered += Number(campaign.stats?.number_of_replied) || 0;
        replyDials += delivered;
      }
    });
    const sentTotal = sends.call + sends.sms + sends.whatsapp + sends.email;
    const sentParts = [
      sends.call && isEnabled("call")
        ? t("outbound.v2.sentCalls", { count: sends.call })
        : null,
      sends.sms && isEnabled("sms")
        ? t("outbound.v2.sentText", { count: sends.sms })
        : null,
      sends.whatsapp && isEnabled("whatsapp")
        ? t("outbound.v2.sentWhatsapp", { count: sends.whatsapp })
        : null,
      sends.email && isEnabled("email")
        ? t("outbound.v2.sentEmail", { count: sends.email })
        : null,
    ].filter(Boolean);

    const replyRate =
      replyDials > 0 ? Math.round((answered / replyDials) * 100) : null;

    return {
      active,
      scheduled,
      finished,
      sentTotal,
      sentNote: sentParts.length ? sentParts.join(" · ") : t("outbound.v2.noSends"),
      replyRate,
      answered,
    };
  }, [rangeCampaigns, t, isEnabled]);

  const handleDelete = (id: string, name: string) => {
    modal.confirm({
      title: t("outbound.deleteTitle"),
      content: t("outbound.deleteConfirm", { name }),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          await dispatch(deleteCampaignThunk(id)).unwrap();
          toast.success(t("outbound.campaignDeletedSuccessfully"), 5);
          fetchCampaigns(currentPage);
        } catch (error) {
          toast.error(t("outbound.failedToDeleteCampaign"), 5);
        }
      },
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: t("outbound.campaignName"),
      dataIndex: "campaign_name",
      key: "campaign_name",
      render: (text: string, record: any) => (
        <NameCell>
          <NameLink
            type="button"
            onClick={() =>
              navigate(`/outbound/${record._id}`, {
                state: { channel: record.channel },
              })
            }
          >
            {text}
          </NameLink>
          <Started>
            {record.createdAt
              ? dayjs(record.createdAt).format("h:mm A, MMM D, YYYY")
              : "—"}
          </Started>
        </NameCell>
      ),
    },
    {
      title: t("outbound.v2.channel"),
      dataIndex: "channel",
      key: "channel",
      render: (channel: string) => (
        <ChannelCell>{channelIcon(normalizeChannel(channel))}</ChannelCell>
      ),
    },
    {
      title: t("outbound.v2.agent"),
      dataIndex: "agent_id",
      key: "agent",
      render: (agent: any) => agent?.agent_name || "—",
    },
    {
      title: t("outbound.v2.recipients"),
      dataIndex: "number_of_recipients",
      key: "recipients",
      render: (value: number, record: any) =>
        dash(
          value ||
            record.stats?.total_recipient ||
            record.stats?.number_of_sent ||
            0
        ),
    },
    {
      title: t("outbound.v2.delivered"),
      key: "delivered",
      render: (_: any, record: any) => {
        const value =
          record.stats?.number_of_sent ?? record.stats?.number_of_calls;
        return value === undefined ? "—" : formatNumber(value);
      },
    },
    {
      title: t("outbound.v2.replied"),
      key: "replied",
      render: (_: any, record: any) => {
        const channel = normalizeChannel(record.channel);
        if (channel === "call") return "—";
        const value = record.stats?.number_of_replied;
        if (value === undefined) return "—";
        return (
          <RepliedLink
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              const params = new URLSearchParams({ campaign_id: record._id });
              if (channel) params.set("channel", channel);
              navigate(`/inbound?${params.toString()}`);
            }}
          >
            {formatNumber(value)}
          </RepliedLink>
        );
      },
    },
    {
      title: t("outbound.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <StatusBadge
          colors={
            statusColors[status] || { text: "#374151", background: "#f3f4f6" }
          }
        >
          {getStatusLabel(status)}
        </StatusBadge>
      ),
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_: any, record: any) => {
        const canEdit =
          record?.status === CAMPAIGN_STATUS.TEST ||
          record?.status === CAMPAIGN_STATUS.SCHEDULE;
        const items = [
          ...(canEdit ? [{ key: "edit", label: t("outbound.edit") }] : []),
          { key: "delete", label: t("common.delete"), danger: true },
        ];
        return (
          <Dropdown
            menu={{
              items,
              onClick: async ({ key }) => {
                if (key === "edit") {
                  setSelectedCampaignId(record._id);
                  setIsScheduleModalOpen(true);
                }
                if (key === "delete") {
                  handleDelete(record._id, record.campaign_name);
                }
              },
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Page>
      <CampaignStatusDropdownStyles />
      <Header>
        <Title>{t("outbound.title")}</Title>
        <HeaderControls>
          <RangeSelect
            value={selectedTimeRange}
            onChange={(value: unknown) => setSelectedTimeRange(value as string)}
            suffixIcon={loading ? <LoadingOutlined /> : <DownOutlined />}
            loading={loading}
          >
            <Option value="24h">{t("dashboard.filters.last24h")}</Option>
            <Option value="7d">{t("dashboard.filters.last7d")}</Option>
            <Option value="30d">{t("dashboard.filters.last30d")}</Option>
            <Option value="90d">{t("dashboard.filters.last90d")}</Option>
          </RangeSelect>
          <CreateButton type="button" onClick={() => setIsModalOpen(true)}>
            <AddIcon /> {t("outbound.createCampaign")}
          </CreateButton>
        </HeaderControls>
      </Header>

      <ContentLoading spinning={loading} minHeight={140}>
      <KpiGrid>
        <KpiCard>
          <KpiLabel>{t("outbound.v2.activeCampaigns")}</KpiLabel>
          <KpiValue>{formatNumber(kpis.active)}</KpiValue>
          <KpiNote>
            {t("outbound.v2.scheduledFinished", {
              scheduled: formatNumber(kpis.scheduled),
              finished: formatNumber(kpis.finished),
            })}
          </KpiNote>
        </KpiCard>
        <KpiCard>
          <KpiLabel>{t("outbound.v2.sent")}</KpiLabel>
          <KpiValue>{formatNumber(kpis.sentTotal)}</KpiValue>
          <KpiNote>{kpis.sentNote}</KpiNote>
        </KpiCard>
        <KpiCard>
          <KpiLabel>{t("outbound.v2.replyRate")}</KpiLabel>
          <KpiValue $accent={kpis.replyRate !== null}>
            {kpis.replyRate === null ? "—" : `${kpis.replyRate}%`}
          </KpiValue>
          <KpiNote>
            {kpis.replyRate === null
              ? t("outbound.v2.noRepliesYet")
              : t("outbound.v2.repliedNote", {
                  count: kpis.answered,
                })}
          </KpiNote>
        </KpiCard>
      </KpiGrid>
      </ContentLoading>

      <CardWrapper>
        <Toolbar>
          <StatusSelect
            value={statusFilter}
            onChange={(value: unknown) => setStatusFilter(value as string)}
            suffixIcon={<DownOutlined />}
            popupMatchSelectWidth
            popupClassName="campaign-status-dropdown"
            menuItemSelectedIcon={null}
            aria-label={t("outbound.status")}
            $isAll={statusFilter === STATUS_FILTER_ALL}
          >
            {STATUS_FILTER_VALUES.map((value) => (
              <Option key={value} value={value}>
                {getStatusLabel(value)}
              </Option>
            ))}
          </StatusSelect>
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={t("outbound.searchCampaign")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </Toolbar>
        <StyledTable
          dataSource={campaigns.campaigns}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: campaigns.pagination.totalItems,
            position: ["bottomCenter"],
            showSizeChanger: false,
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current || 1);
          }}
          rowKey="_id"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                }
              />
            ),
          }}
        />
      </CardWrapper>
      <CreateCampaignModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchCampaigns(1);
        }}
      />
      <ScheduleCampaignModal
        open={isScheduleModalOpen}
        onCancel={() => {
          setIsScheduleModalOpen(false);
          setSelectedCampaignId(null);
        }}
        onSchedule={async (utcStartAt: string, tz: string) => {
          if (!selectedCampaignId) return;
          try {
            await dispatch(
              updateCampaignThunk({
                id: selectedCampaignId,
                payload: {
                  start_at: utcStartAt,
                  time_zone: tz,
                  status: CAMPAIGN_STATUS.SCHEDULE,
                },
              })
            ).unwrap();
            toast.success(
              t("outbound.scheduleUpdatedSuccessfully"),
              5,
              <CheckCircleIcon />
            );
            setIsScheduleModalOpen(false);
            setSelectedCampaignId(null);
            await fetchCampaigns();
          } catch (error) {
            toast.error(t("outbound.failedToUpdateSchedule"));
          }
        }}
      />
    </Page>
  );
};

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${pageShellCss}
`;

const Header = styled.div`
  ${wrapRowCss}
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  color: #000;
  ${pageTitleCss}
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const RangeSelect = styled(Select)`
  && {
    width: 180px;
    height: 42px;
    max-width: 100%;
  }

  @media (max-width: 640px) {
    && {
      width: 100%;
      flex: 1 1 100%;
    }
  }

  .ant-select-selector {
    box-sizing: border-box !important;
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 8px !important;
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 14px !important;
  }

  .ant-select-selector:focus,
  .ant-select-selector:active {
    box-shadow: none !important;
  }

  .ant-select-selection-wrap {
    height: 100%;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    font-size: 16px;
  }

  .ant-select-arrow {
    inset-inline-end: 14px;
    margin-top: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 42px;
  min-height: 42px;
  padding: 0 16px;
  font-size: 16px;
  line-height: 1;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    display: block;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover {
    background: #047857;
  }
  ${filterControlCss}

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${kpiBreakCss}
`;

const KpiCard = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const KpiLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${theme.colors.grey1};
`;

const KpiValue = styled.span<{ $accent?: boolean }>`
  font-weight: 700;
  font-size: 32px;
  line-height: 1;
  color: ${({ $accent }) => ($accent ? theme.colors.green1 : "#000")};

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const KpiNote = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.grey1};
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  ${tableScrollCss}
`;

const Toolbar = styled.div`
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusSelect = styled(Select)<{ $isAll?: boolean }>`
  && {
    width: 160px;
    max-width: 100%;
    height: 40px;
  }

  @media (max-width: 640px) {
    && {
      width: 100%;
    }
  }

  .ant-select-selector {
    box-sizing: border-box !important;
    height: 40px !important;
    min-height: 40px !important;
    border-radius: 8px !important;
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 12px !important;
  }

  .ant-select-selector:focus,
  .ant-select-selector:active,
  &.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
    border-color: ${theme.colors.grey2} !important;
  }

  .ant-select-selection-wrap {
    height: 100%;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    font-size: 14px;
    color: ${({ $isAll }) =>
      $isAll ? theme.colors.grey1 : "#111827"} !important;
  }

  .ant-select-arrow {
    inset-inline-end: 12px;
    margin-top: 0;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.grey1};
    font-size: 12px;
  }
`;

const CampaignStatusDropdownStyles = createGlobalStyle`
  .campaign-status-dropdown.ant-select-dropdown {
    padding: 4px 0;
    border-radius: 8px;
    border: 1px solid ${theme.colors.grey2};
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
    overflow: hidden;
  }

  .campaign-status-dropdown .ant-select-item {
    padding: 8px 12px;
    border-radius: 0;
    font-size: 14px;
    line-height: 20px;
    color: #111827;
    background: #fff !important;
  }

  .campaign-status-dropdown .ant-select-item-option-selected {
    background: #e6f4ff !important;
    font-weight: 700;
    color: #111827;
  }

  .campaign-status-dropdown .ant-select-item-option-active:not(.ant-select-item-option-selected) {
    background: #f5f5f5 !important;
  }

  .campaign-status-dropdown .ant-select-item-option-state {
    display: none;
  }
`;

const SearchWrapper = styled.div`
  ${searchFieldCss}
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
  flex: none;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NameLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  color: #1890ff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
`;

const RepliedLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #1890ff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const Started = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;

const ChannelCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #1f1f1f;
`;

const StatusBadge = styled.span<{
  colors: { text: string; background: string };
}>`
  background: ${({ colors }) => colors.background};
  color: ${({ colors }) => colors.text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
    height: 64px;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default Outbound;
